import React, { useCallback, useEffect, useRef, useState } from "react";
import AttributeMenu from "./AttributeMenu";
import { nrqlFragment } from "./nrqlFilter";

/**
 * The full log event behind a row, expanded in place beneath it.
 *
 * In place rather than in a modal: this already sits three layers deep — market
 * view, entity panel, host — and a dialog over a panel over a page is a lot of
 * chrome for "show me the rest of this line". Expanding keeps the surrounding
 * rows visible, which is most of why you were reading a log in the first place,
 * survives a 448px panel, and needs no focus trap. It also matches how the
 * service cards and alert groups already behave.
 *
 * The record is the one already in the table — the log query selects `*`, so
 * every attribute travels with the row. Re-querying on click would need a way
 * to name a single record, and NRQL has none that is reliable: two events can
 * share a millisecond timestamp, so a lookup could return a different line from
 * the one that was clicked.
 */

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Renders a value as coloured JSON.
 *
 * Built from elements rather than a highlighted HTML string: these values come
 * from log records, so anything routed through innerHTML would be markup the
 * log author chose. React escapes text nodes for free.
 */
function JsonValue({ value, indent, path = [], onAttribute }) {
  const pad = "  ".repeat(indent);
  const padInner = "  ".repeat(indent + 1);

  if (value === null) return <span className="json-null">null</span>;
  if (typeof value === "boolean")
    return <span className="json-bool">{String(value)}</span>;
  if (typeof value === "number")
    return <span className="json-number">{String(value)}</span>;
  if (typeof value === "string")
    return <span className="json-string">"{value}"</span>;

  if (Array.isArray(value)) {
    if (!value.length) return <span className="json-punct">[]</span>;
    return (
      <>
        <span className="json-punct">[</span>
        {value.map((item, i) => (
          <React.Fragment key={i}>
            {"\n"}
            {padInner}
            <JsonValue
              value={item}
              indent={indent + 1}
              path={[...path, i]}
              onAttribute={onAttribute}
            />
            {i < value.length - 1 ? <span className="json-punct">,</span> : null}
          </React.Fragment>
        ))}
        {"\n"}
        {pad}
        <span className="json-punct">]</span>
      </>
    );
  }

  if (isObject(value)) {
    const keys = Object.keys(value);
    if (!keys.length) return <span className="json-punct">{"{}"}</span>;
    return (
      <>
        <span className="json-punct">{"{"}</span>
        {keys.map((key, i) => (
          <React.Fragment key={key}>
            {"\n"}
            {padInner}
            {/* The pair is the target, not the key alone: a filter needs both,
                and a one-word hit area on a 0.6875rem key is a poor one. */}
            <span
              className="json-entry"
              tabIndex={0}
              role="button"
              aria-label={`Attribute ${[...path, key].join(".")}`}
              onContextMenu={(e) => onAttribute?.(e, [...path, key], value[key])}
              onKeyDown={(e) => {
                // Right-click alone is unreachable without a mouse. The
                // context-menu key and Enter open the same menu.
                if (e.key === "ContextMenu" || e.key === "Enter") {
                  e.preventDefault();
                  onAttribute?.(e, [...path, key], value[key]);
                }
              }}
            >
              <span className="json-key">"{key}"</span>
              <span className="json-punct">: </span>
              <JsonValue
                value={value[key]}
                indent={indent + 1}
                path={[...path, key]}
                onAttribute={onAttribute}
              />
            </span>
            {i < keys.length - 1 ? <span className="json-punct">,</span> : null}
          </React.Fragment>
        ))}
        {"\n"}
        {pad}
        <span className="json-punct">{"}"}</span>
      </>
    );
  }

  return <span>{String(value)}</span>;
}

export default function LogEventDetail({ event, onClose, onFilter }) {
  const [copied, setCopied] = useState(null);
  const [menu, setMenu] = useState(null);
  const closeRef = useRef(null);

  /**
   * Right-click (or the context-menu key) on an attribute.
   *
   * Filtering only makes sense against a single value, so an object or array
   * gets the menu with those two entries disabled rather than no menu at all —
   * a target that silently does nothing is worse than one that says why.
   */
  const openAttributeMenu = useCallback(
    (domEvent, path, value) => {
      domEvent.preventDefault();
      domEvent.stopPropagation();

      const name = path.join(".");
      const box = domEvent.currentTarget?.getBoundingClientRect?.();
      setMenu({
        // A keyboard press has no pointer position; fall back to the element.
        x: domEvent.clientX || (box ? box.left : 0),
        y: domEvent.clientY || (box ? box.bottom : 0),
        name,
        value,
        filterable: value === null || typeof value !== "object",
      });
    },
    []
  );

  const closeMenu = useCallback(() => setMenu(null), []);

  const menuItems = menu
    ? [
        {
          label: `Filter by ${menu.name}`,
          hint: menu.filterable ? "=" : "not a single value",
          disabled: !menu.filterable,
          onSelect: () => onFilter?.(nrqlFragment(menu.name, menu.value)),
        },
        {
          label: `Exclude ${menu.name}`,
          hint: menu.filterable ? "!=" : "not a single value",
          disabled: !menu.filterable,
          onSelect: () =>
            onFilter?.(nrqlFragment(menu.name, menu.value, { negate: true })),
        },
        {
          label: "Copy value",
          onSelect: () => {
            const text =
              typeof menu.value === "object" && menu.value !== null
                ? JSON.stringify(menu.value, null, 2)
                : String(menu.value);
            navigator.clipboard?.writeText(text).catch(() => {});
          },
        },
      ]
    : [];

  useEffect(() => {
    /**
     * Capture phase, deliberately. The entity panel listens for Escape on
     * document and closes itself; without capturing first, dismissing this
     * would take the whole panel with it.
     */
    const onKey = (keyEvent) => {
      if (keyEvent.key !== "Escape") return;
      keyEvent.stopPropagation();
      keyEvent.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  const copy = async () => {
    const text = JSON.stringify(event, null, 2);
    try {
      // Not available on an insecure origin, and a browser may refuse it
      // outright — say so rather than looking like nothing happened.
      await navigator.clipboard.writeText(text);
      setCopied("Copied");
    } catch {
      setCopied("Copy blocked");
    }
  };

  return (
    <div className="log-detail">
      <div className="log-detail__bar">
        <span className="log-detail__title">
          {Object.keys(event).length} attributes
        </span>
        <span className="log-detail__status" aria-live="polite">
          {copied}
        </span>
        <button type="button" className="log-detail__btn" onClick={copy}>
          Copy JSON
        </button>
        <button
          type="button"
          className="log-detail__btn"
          onClick={onClose}
          ref={closeRef}
        >
          Close
        </button>
      </div>

      <pre className="log-detail__json">
        <code>
          <JsonValue
            value={event}
            indent={0}
            onAttribute={onFilter ? openAttributeMenu : undefined}
          />
        </code>
      </pre>

      {onFilter ? (
        <p className="log-detail__hint">
          Right-click an attribute to filter by it.
        </p>
      ) : null}

      {menu ? (
        <AttributeMenu
          x={menu.x}
          y={menu.y}
          items={menuItems}
          onClose={closeMenu}
        />
      ) : null}
    </div>
  );
}
